// Utility for fetching and computing dynamic shipping rules across the customer store

const DEFAULT_STATE_RULES = [
  {
    id: "ap_tg",
    name: "Andhra Pradesh & Telangana",
    states: ["Andhra Pradesh", "Telangana"],
    rates: {
      "1": 60,
      "2": 100,
      "3": 120,
      "4": 150,
      "5": 200,
      "6": 250,
      "7": 280,
      "8": 320,
      "9": 350,
      "10": 400,
    },
  },
  {
    id: "rest_of_india",
    name: "Rest of India / Other States (Karnataka, Tamil Nadu, etc.)",
    states: [],
    is_default: true,
    rates: {
      "1": 120,
      "2": 150,
      "3": 200,
      "4": 250,
      "5": 300,
      "6": 350,
      "7": 400,
      "8": 450,
      "9": 500,
      "10": 500,
    },
  },
];

const DEFAULT_SETTINGS = {
  free_shipping_threshold: 0,
  shipping_fee: 60,
  is_enabled: true,
  state_rules: DEFAULT_STATE_RULES,
};

const API_CANDIDATES = [
  "http://localhost:5000/shipping/settings",
  "http://127.0.0.1:5000/shipping/settings",
  "https://api.vanyacollectionsrajahmundry.com/shipping/settings",
];

export const getCachedShippingSettings = () => {
  try {
    const raw = localStorage.getItem("vanya_shipping_settings");
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        free_shipping_threshold: Number(
          parsed.free_shipping_threshold ?? DEFAULT_SETTINGS.free_shipping_threshold
        ),
        shipping_fee: Number(
          parsed.shipping_fee ?? DEFAULT_SETTINGS.shipping_fee
        ),
        is_enabled:
          parsed.is_enabled !== undefined
            ? Boolean(parsed.is_enabled)
            : DEFAULT_SETTINGS.is_enabled,
        state_rules: Array.isArray(parsed.state_rules) && parsed.state_rules.length > 0
          ? parsed.state_rules
          : DEFAULT_STATE_RULES,
      };
    }
  } catch (e) {
    // Ignore JSON errors
  }
  return DEFAULT_SETTINGS;
};

export const fetchShippingSettings = async () => {
  // Try each API candidate
  for (const url of API_CANDIDATES) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);

      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.settings) {
          const settings = {
            free_shipping_threshold: Number(
              data.settings.free_shipping_threshold ?? DEFAULT_SETTINGS.free_shipping_threshold
            ),
            shipping_fee: Number(
              data.settings.shipping_fee ?? DEFAULT_SETTINGS.shipping_fee
            ),
            is_enabled:
              data.settings.is_enabled !== undefined
                ? Boolean(data.settings.is_enabled)
                : DEFAULT_SETTINGS.is_enabled,
            state_rules:
              Array.isArray(data.settings.state_rules) && data.settings.state_rules.length > 0
                ? data.settings.state_rules
                : DEFAULT_STATE_RULES,
          };
          try {
            localStorage.setItem("vanya_shipping_settings", JSON.stringify(settings));
          } catch (e) {}
          return settings;
        }
      }
    } catch (err) {
      // Continue to next candidate
    }
  }

  // Fallback to cache or defaults
  return getCachedShippingSettings();
};

export const calculateShipping = (
  subtotal,
  settings = null,
  state = "",
  totalPieces = 1
) => {
  const currentSettings = settings || getCachedShippingSettings();
  const numSubtotal = Number(subtotal || 0);
  const threshold = Number(currentSettings.free_shipping_threshold ?? 0);
  const defaultFee = Number(currentSettings.shipping_fee ?? 60);
  const enabled = currentSettings.is_enabled !== false;
  const pieces = Math.max(1, Number(totalPieces) || 1);
  const cleanState = String(state || "").trim().toLowerCase();

  // If free shipping rule is enabled, threshold > 0, and subtotal >= threshold
  if (enabled && threshold > 0 && numSubtotal >= threshold) {
    return {
      shippingCost: 0,
      isFree: true,
      difference: 0,
      threshold,
      fee: defaultFee,
      matchedRule: "Free Delivery Above Threshold",
      totalPieces: pieces,
    };
  }

  // Match state rule
  const stateRules = Array.isArray(currentSettings.state_rules) && currentSettings.state_rules.length > 0
    ? currentSettings.state_rules
    : DEFAULT_STATE_RULES;

  let matchedRule = null;

  if (cleanState) {
    matchedRule = stateRules.find((rule) => {
      if (rule.is_default || !Array.isArray(rule.states)) return false;
      return rule.states.some((s) => {
        const norm = String(s || "").trim().toLowerCase();
        return norm === cleanState || cleanState.includes(norm) || norm.includes(cleanState);
      });
    });
  }

  // Fallback to default rule (e.g. Rest of India) if not matched
  if (!matchedRule) {
    matchedRule = stateRules.find((rule) => rule.is_default) || stateRules[stateRules.length - 1];
  }

  let finalFee = defaultFee;
  if (matchedRule && matchedRule.rates) {
    const tierKey = String(Math.min(pieces, 10));
    if (matchedRule.rates[tierKey] !== undefined && matchedRule.rates[tierKey] !== null) {
      finalFee = Number(matchedRule.rates[tierKey]);
    } else if (matchedRule.rates["1"] !== undefined) {
      finalFee = Number(matchedRule.rates["1"]);
    }
  }

  return {
    shippingCost: finalFee,
    isFree: finalFee === 0,
    difference: threshold > 0 ? Math.max(0, threshold - numSubtotal) : 0,
    threshold,
    fee: finalFee,
    matchedRule: matchedRule ? matchedRule.name : "Standard Delivery",
    totalPieces: pieces,
  };
};
